// components/Accountability/Partner/CallFromPartner.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-hot-toast";
import panda from "../../../../../assets/panda-partner.png";
import { apiFetch } from "../../../../../services/apiFetch";
import { openRazorpayCheckout } from "../../../../../utils/razorpayCheckout";

const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL || "http://localhost:8000/api";
const ACCOUNTABILITY_BOOKING_SOURCE = "accountability_booking";
const ACCOUNTABILITY_RETURN_STORAGE_KEY = "accountability_booking_return";
const PAYMENT_RETURN_PARAMS = [
  "source",
  "booking_id",
  "payment_intent_id",
  "course_id",
  "module_id",
  "module_slug",
  "subsection_id",
  "subsection_slug",
  "subsection_url",
  "razorpay_payment_id",
  "razorpay_payment_link_id",
  "razorpay_payment_link_reference_id",
  "razorpay_payment_link_status",
  "razorpay_signature",
  "accountability_booking",
];

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null") || {};
  } catch {
    return {};
  }
};

const isValidPhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 14;
};

const formatSlot = (value) => {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
};

const formatSlotDate = (value) =>
  new Intl.DateTimeFormat("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(value));

const formatSlotTime = (value) =>
  new Intl.DateTimeFormat("en-IN", {
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));

const localDateKey = (value) => {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const googleCalendarDate = (value) =>
  new Date(value).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");

const buildGoogleCalendarUrl = (booking) => {
  if (!booking?.slot_start || !booking?.slot_end) return "";
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: "Accountability Partner Call",
    dates: `${googleCalendarDate(booking.slot_start)}/${googleCalendarDate(booking.slot_end)}`,
    details: "45-minute accountability call.",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const formatCurrency = (amount, currency = "INR") => {
  if (!amount) return "";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount / 100);
};

const removePaymentReturnParams = (url) => {
  PAYMENT_RETURN_PARAMS.forEach((param) => url.searchParams.delete(param));
};

const readStoredPaymentReturn = () => {
  try {
    return JSON.parse(
      sessionStorage.getItem(ACCOUNTABILITY_RETURN_STORAGE_KEY) || "null"
    );
  } catch {
    return null;
  }
};

const buildPaymentReturnFromSearch = () => {
  const params = new URLSearchParams(window.location.search);
  if (
    params.get("source") !== ACCOUNTABILITY_BOOKING_SOURCE &&
    params.get("accountability_booking") !== "1"
  ) {
    return null;
  }
  return PAYMENT_RETURN_PARAMS.reduce((acc, key) => {
    const value = params.get(key);
    if (value) acc[key] = value;
    return acc;
  }, {});
};

const clearStoredPaymentReturn = () => {
  sessionStorage.removeItem(ACCOUNTABILITY_RETURN_STORAGE_KEY);
};

const getPayloadBooking = (value) => {
  const source = value?.payload || value || {};
  if (source.booking) return source.booking;
  if (!source.booking_id) return null;

  return {
    booking_id: source.booking_id,
    payment_intent_id: source.payment_intent_id,
    status: source.booking_status || "confirmed",
    slot_start: source.slot_start,
    slot_end: source.slot_end,
    calendar_event_id: source.calendar_event_id,
    calendar_link: source.calendar_link,
    discussion_agenda: source.discussion_agenda,
  };
};
const getPayloadDiscussion = (value) =>
  value?.payload?.discussion_agenda || value?.discussion_agenda || "";

const CallFromPartner = ({
  subsection,
  userId,
  saveProgress,
  onComplete,
  actions,
  payload,
}) => {
  const storedUser = useMemo(() => getStoredUser(), []);
  const storedPhone = useMemo(
    () => storedUser?.phone || storedUser?.phone_number || storedUser?.mobile || "",
    [storedUser]
  );
  const showPhoneInput = storedUser?.phone_available !== true;
  const [slots, setSlots] = useState([]);
  const [bookingPrice, setBookingPrice] = useState(null);
  const [bookingCurrency, setBookingCurrency] = useState("INR");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(payload?.booking || null);
  const [phone, setPhone] = useState(storedPhone);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [discussionAgenda, setDiscussionAgenda] = useState(() =>
    getPayloadDiscussion(payload)
  );
  const [discussionSaving, setDiscussionSaving] = useState(false);
  const [error, setError] = useState("");
  const [showSlotPicker, setShowSlotPicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [visibleMonth, setVisibleMonth] = useState(() => new Date());

  const slotsByDate = useMemo(
    () =>
      slots.reduce((groups, slot) => {
        const key = localDateKey(slot.start);
        if (!groups[key]) groups[key] = [];
        groups[key].push(slot);
        return groups;
      }, {}),
    [slots]
  );
  const calendarDays = useMemo(() => {
    const year = visibleMonth.getFullYear();
    const month = visibleMonth.getMonth();
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return [
      ...Array(firstWeekday).fill(null),
      ...Array.from({ length: daysInMonth }, (_, index) => new Date(year, month, index + 1)),
    ];
  }, [visibleMonth]);

  const openSlotPicker = useCallback(() => {
    const slotToShow = selectedSlot || slots[0];
    if (slotToShow) {
      setSelectedDate(localDateKey(slotToShow.start));
      setVisibleMonth(new Date(slotToShow.start));
    }
    setShowSlotPicker(true);
  }, [selectedSlot, slots]);

  useEffect(() => {
    actions?.setPrimary({ visible: false });
    return () => actions?.clearPrimary?.();
  }, [actions]);

  useEffect(() => {
    setBooking(getPayloadBooking(payload));
    setDiscussionAgenda(getPayloadDiscussion(payload));
  }, [payload]);

  const loadSlots = useCallback(async () => {
    setLoadingSlots(true);
    setError("");
    try {
      const res = await apiFetch(`${apiBaseURL}/accountability/bookings/slots`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || "Failed to load booking slots.");
      }
      const availableSlots = data.slots || [];
      setSlots(availableSlots);
      setBookingPrice(data.amount || null);
      setBookingCurrency(data.currency || "INR");
      const firstSlot = availableSlots[0] || null;
      setSelectedSlot(firstSlot);
      setSelectedDate(firstSlot ? localDateKey(firstSlot.start) : "");
      if (firstSlot) setVisibleMonth(new Date(firstSlot.start));
    } catch (err) {
      setError(err?.message || "Failed to load booking slots.");
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  useEffect(() => {
    if (!showSlotPicker) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setShowSlotPicker(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showSlotPicker]);

  const handleDiscussionSubmit = useCallback(async () => {
    const trimmedAgenda = discussionAgenda.trim();
    if (!trimmedAgenda) {
      setError("Please share what you would like to discuss in the call.");
      return;
    }

    setDiscussionSaving(true);
    setError("");
    try {
      const res = await apiFetch(
        `${apiBaseURL}/accountability/bookings/${booking.booking_id}/discussion`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ discussion_agenda: trimmedAgenda }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || "Failed to save your call notes.");
      }
      setBooking(data);
      setDiscussionAgenda(data.discussion_agenda || trimmedAgenda);
      toast.success("Your call notes are saved.", {
        style: { background: "#54B0AF", color: "#fff" },
        iconTheme: { primary: "#ffffff", secondary: "#54B0AF" },
      });
      onComplete?.();
    } catch (err) {
      setError(err?.message || "Failed to save your call notes.");
    } finally {
      setDiscussionSaving(false);
    }
  }, [booking?.booking_id, discussionAgenda, onComplete]);

  const handleSkipBooking = useCallback(async () => {
    setError("");
    try {
      await saveProgress("completed", {
        alerted: false,
        booking_status: "skipped",
        skipped_booking: true,
      });
      onComplete?.();
    } catch (err) {
      setError(err?.message || "Failed to save your progress.");
    }
  }, [onComplete, saveProgress]);

  const verifyPaymentReturn = useCallback(async () => {
    const paymentReturn = buildPaymentReturnFromSearch() || readStoredPaymentReturn();
    const isAccountabilityReturn =
      paymentReturn?.source === ACCOUNTABILITY_BOOKING_SOURCE ||
      paymentReturn?.accountability_booking === "1";
    if (!isAccountabilityReturn) return;
    if (
      paymentReturn.subsection_id &&
      String(paymentReturn.subsection_id) !== String(subsection?.id)
    ) {
      return;
    }
    if (
      paymentReturn.subsection_url &&
      paymentReturn.subsection_url !== subsection?.subsection_url
    ) {
      return;
    }
    const payload = {
      payment_link_id: paymentReturn.razorpay_payment_link_id,
      payment_link_reference_id: paymentReturn.razorpay_payment_link_reference_id,
      payment_link_status: paymentReturn.razorpay_payment_link_status,
      payment_id: paymentReturn.razorpay_payment_id,
      signature: paymentReturn.razorpay_signature,
    };
    if (!payload.payment_id || !payload.signature) return;

    setVerifyLoading(true);
    setError("");
    try {
      const res = await apiFetch(`${apiBaseURL}/accountability/bookings/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || "Payment verification failed.");
      }
      setBooking(data);
      if (data.status === "confirmed") {
        toast.success("Your accountability call is booked.", {
          style: { background: "#54B0AF", color: "#fff" },
          iconTheme: { primary: "#ffffff", secondary: "#54B0AF" },
        });
      } else if (data.status === "paid_needs_reschedule") {
        toast.error("Payment received, but that slot is no longer available. Please choose another slot.");
        loadSlots();
      }
      const cleanUrl = new URL(window.location.href);
      removePaymentReturnParams(cleanUrl);
      clearStoredPaymentReturn();
      window.history.replaceState({}, "", `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
    } catch (err) {
      setError(err?.message || "Payment verification failed.");
    } finally {
      setVerifyLoading(false);
    }
  }, [loadSlots, subsection?.id, subsection?.subsection_url]);

  useEffect(() => {
    verifyPaymentReturn();
  }, [verifyPaymentReturn]);

  const handleBook = useCallback(async () => {
    if (!selectedSlot || bookingLoading) return;
    const isPaidReschedule = booking?.status === "paid_needs_reschedule";

    setBookingLoading(true);
    setError("");
    try {
      if (isPaidReschedule) {
        const res = await apiFetch(
          `${apiBaseURL}/accountability/bookings/${booking.booking_id}/reschedule`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              slot_start: selectedSlot.start_utc || selectedSlot.start,
              slot_end: selectedSlot.end_utc || selectedSlot.end,
            }),
          }
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.detail || "Failed to book the new slot.");
        }
        setBooking(data);
        toast.success("Your accountability call is booked.", {
          style: { background: "#54B0AF", color: "#fff" },
          iconTheme: { primary: "#ffffff", secondary: "#54B0AF" },
        });
        setBookingLoading(false);
        return;
      }

      const callbackUrl = new URL(window.location.href);
      removePaymentReturnParams(callbackUrl);
      const requestBody = {
        user_id: userId,
        subsection_id: subsection.id,
        slot_start: selectedSlot.start_utc || selectedSlot.start,
        slot_end: selectedSlot.end_utc || selectedSlot.end,
        callback_url: callbackUrl.toString(),
      };
      if (isValidPhone(phone)) {
        requestBody.phone = phone;
      }
      const res = await apiFetch(`${apiBaseURL}/accountability/bookings/order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || "Failed to create payment link.");
      }
      if (!data.order_id) throw new Error("Payment order was not returned.");
      const confirmed = await openRazorpayCheckout({
        order: data,
        verify: async (result) => {
          const verifyRes = await apiFetch(`${apiBaseURL}/accountability/bookings/order/verify`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ order_id: result.razorpay_order_id, payment_id: result.razorpay_payment_id, signature: result.razorpay_signature }),
          });
          const verified = await verifyRes.json().catch(() => ({}));
          if (!verifyRes.ok) throw new Error(verified.detail || "Payment verification failed.");
          return verified;
        },
      });
      setBooking(confirmed);
      if (confirmed.status === "paid_needs_reschedule") loadSlots();
      setBookingLoading(false);
    } catch (err) {
      setError(err?.message || "Failed to create payment link.");
      setBookingLoading(false);
    }
  }, [
    booking?.booking_id,
    booking?.status,
    bookingLoading,
    loadSlots,
    phone,
    selectedSlot,
    subsection?.id,
    userId,
  ]);

  const isConfirmed = booking?.status === "confirmed";
  const priceDisplay = formatCurrency(
    booking?.amount || bookingPrice,
    booking?.currency || bookingCurrency
  );
  const addToGoogleCalendarUrl = buildGoogleCalendarUrl(booking);

  return (
    <div className="flex flex-col items-center text-center space-y-6">
      <img
        src={panda}
        alt="Accountability Partner"
        className="w-[300px] h-[315px] object-contain mx-auto"
      />
      <div className="max-w-xl space-y-3">
        <p className="text-gray-600 text-[16px] leading-relaxed">
          Book a 45-minute accountability call after payment. Your partner will
          help you align, plan ahead, and keep moving toward your goals.
        </p>
        {priceDisplay && (
          <p className="text-[#1F2937] font-semibold">Price: {priceDisplay}</p>
        )}
      </div>

      {isConfirmed ? (
        <div className="w-full max-w-xl space-y-4">
          <div className="rounded-[16px] border border-[#B7E3DF] bg-[#F3FBFA] px-5 py-4 text-left">
            <p className="text-[#0F766E] font-semibold">Call booked</p>
            <p className="text-gray-700 mt-1">{formatSlot(booking.slot_start)}</p>
            {addToGoogleCalendarUrl && (
              <a
                className="inline-block mt-3 text-[#0F766E] font-semibold underline"
                href={addToGoogleCalendarUrl}
                target="_blank"
                rel="noreferrer"
              >
                Add to Google Calendar
              </a>
            )}
          </div>
          <div className="text-left space-y-3">
            <label
              className="block text-[#1F2937] font-semibold"
              htmlFor="accountability-discussion-agenda"
            >
              What would you like to discuss in this call?
            </label>
            <textarea
              id="accountability-discussion-agenda"
              value={discussionAgenda}
              onChange={(event) => setDiscussionAgenda(event.target.value)}
              rows={4}
              placeholder="Share the goals, blockers, or decisions you want help with."
              className="w-full rounded-[14px] border border-gray-300 px-4 py-3 outline-none focus:border-[#54B0AF] text-left"
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="button"
              onClick={handleDiscussionSubmit}
              disabled={discussionSaving || verifyLoading}
              className="bg-[#54B0AF] font-gotham font-medium hover:bg-teal-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-6 py-[18px] rounded-[18px] shadow-md transition-all"
              style={{ width: "337px" }}
            >
              {discussionSaving ? "Saving..." : "Save and Continue"}
            </button>
          </div>
        </div>
      ) : (
        <div className="w-full max-w-xl space-y-4">
          {loadingSlots ? (
            <p className="text-gray-500">Loading available slots...</p>
          ) : slots.length ? (
            <button
              type="button"
              onClick={openSlotPicker}
              className="w-full rounded-[16px] border border-[#B7E3DF] bg-white px-5 py-4 text-left transition hover:border-[#54B0AF] hover:bg-[#F7FCFB]"
            >
              <span className="block text-sm font-semibold text-[#0F766E]">
                {selectedSlot ? "Selected time" : "Choose a time"}
              </span>
              <span className="mt-1 block font-semibold text-gray-800">
                {selectedSlot ? formatSlot(selectedSlot.start) : "View all available slots"}
              </span>
              <span className="mt-1 block text-sm text-gray-500">
                {slots.length} available {slots.length === 1 ? "slot" : "slots"} · Change
              </span>
            </button>
          ) : (
            <div className="rounded-[14px] border border-gray-200 bg-gray-50 px-4 py-4 text-gray-600">
              No slots are currently available. Please check again later.
            </div>
          )}

          {showPhoneInput && (
            <input
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder="Phone number for payment"
              className="w-full rounded-[14px] border border-gray-300 px-4 py-3 outline-none focus:border-[#54B0AF]"
            />
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
          {verifyLoading && <p className="text-sm text-gray-500">Verifying payment...</p>}

          <button
            onClick={handleBook}
            disabled={!selectedSlot || bookingLoading || verifyLoading}
            className="mt-2 bg-[#54B0AF] font-gotham font-medium hover:bg-teal-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-6 py-[18px] rounded-[18px] shadow-md transition-all"
            style={{ width: "337px" }}
          >
            {bookingLoading
              ? "Preparing..."
              : booking?.status === "paid_needs_reschedule"
                ? "Book New Slot"
                : "Pay and Book Call"}
          </button>
          <button
            type="button"
            onClick={handleSkipBooking}
            disabled={bookingLoading || verifyLoading}
            className="block mx-auto text-[#4B5563] hover:text-[#0F766E] disabled:opacity-60 disabled:cursor-not-allowed font-gotham font-medium underline underline-offset-4 transition-colors"
          >
            I&apos;m good for now
          </button>
        </div>
      )}

      {showSlotPicker && createPortal((
        <div
          className="fixed inset-0 z-[99999] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-6"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowSlotPicker(false);
          }}
        >
          <div
            className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[24px] bg-white text-left shadow-2xl sm:rounded-[24px]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="accountability-slot-picker-title"
          >
            <div className="flex items-start justify-between border-b border-gray-100 px-5 py-5 sm:px-7">
              <div>
                <h2 id="accountability-slot-picker-title" className="text-xl font-semibold text-gray-900">
                  Choose a time
                </h2>
                <p className="mt-1 text-sm text-gray-500">45-minute accountability call · Times shown in your local timezone</p>
              </div>
              <button
                type="button"
                onClick={() => setShowSlotPicker(false)}
                className="ml-4 rounded-full px-3 py-1.5 text-2xl leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close time picker"
              >
                ×
              </button>
            </div>
            <div className="grid min-h-0 flex-1 overflow-y-auto md:grid-cols-[1.15fr_0.85fr] md:overflow-hidden">
              <div className="border-b border-gray-100 px-5 py-5 sm:px-7 md:overflow-y-auto md:border-b-0 md:border-r">
                <div className="mb-5 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">
                    {new Intl.DateTimeFormat("en-IN", { month: "long", year: "numeric" }).format(visibleMonth)}
                  </h3>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      aria-label="Previous month"
                      onClick={() => setVisibleMonth((value) => new Date(value.getFullYear(), value.getMonth() - 1, 1))}
                      className="h-9 w-9 rounded-full text-xl text-gray-600 hover:bg-gray-100"
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      aria-label="Next month"
                      onClick={() => setVisibleMonth((value) => new Date(value.getFullYear(), value.getMonth() + 1, 1))}
                      className="h-9 w-9 rounded-full text-xl text-gray-600 hover:bg-gray-100"
                    >
                      ›
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-7 text-center text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <span key={day} className="py-2">{day.slice(0, 1)}</span>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-y-2 text-center">
                  {calendarDays.map((date, index) => {
                    if (!date) return <span key={`empty-${index}`} />;
                    const dateKey = localDateKey(date);
                    const available = Boolean(slotsByDate[dateKey]?.length);
                    const active = selectedDate === dateKey;
                    return (
                      <button
                        key={dateKey}
                        type="button"
                        disabled={!available}
                        onClick={() => {
                          setSelectedDate(dateKey);
                          setSelectedSlot(slotsByDate[dateKey][0]);
                        }}
                        className={`mx-auto flex h-10 w-10 items-center justify-center rounded-full text-sm transition ${
                          active
                            ? "bg-[#54B0AF] font-semibold text-white shadow-sm"
                            : available
                              ? "font-semibold text-[#0F766E] hover:bg-[#E9F7F6]"
                              : "cursor-default text-gray-300"
                        }`}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-5 text-center text-xs text-gray-400">Available dates are highlighted</p>
              </div>

              <div className="px-5 py-5 sm:px-7 md:overflow-y-auto">
                <h3 className="mb-4 font-semibold text-gray-900">
                  {slotsByDate[selectedDate]?.length
                    ? formatSlotDate(slotsByDate[selectedDate][0].start)
                    : "Select an available date"}
                </h3>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
                  {(slotsByDate[selectedDate] || []).map((slot) => {
                    const active = selectedSlot?.start === slot.start;
                    return (
                      <button
                        key={`${slot.start}-${slot.end}`}
                        type="button"
                        onClick={() => setSelectedSlot(slot)}
                        className={`rounded-[12px] border px-3 py-3 text-center font-semibold transition ${
                          active
                            ? "border-[#54B0AF] bg-[#54B0AF] text-white shadow-sm"
                            : "border-gray-200 bg-white text-[#0F766E] hover:border-[#54B0AF] hover:bg-[#F3FBFA]"
                        }`}
                      >
                        {formatSlotTime(slot.start)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="border-t border-gray-100 bg-white px-5 py-4 sm:px-7">
              <button
                type="button"
                onClick={() => setShowSlotPicker(false)}
                disabled={!selectedSlot}
                className="w-full rounded-[14px] bg-[#54B0AF] px-5 py-3.5 font-semibold text-white transition hover:bg-teal-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {selectedSlot ? `Continue with ${formatSlot(selectedSlot.start)}` : "Select a date and time"}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
};

export default CallFromPartner;
